import React, { useEffect, useState, useMemo } from 'react';

import { Box, PaletteMode, ThemeProvider, createTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import dayjs from 'dayjs';

import { RootState, useAppDispatch, useAppSelector } from './store/store';
import {
  fetchMyDetails,
  fetchMyId,
  rtkFetchUserInfo,
  loadUserAndJobInfo,
  setUserData,
  updateLikedJobsAndAppliedJobs,
  setUserEmail,
  fetchAccountEmail,
} from './store/slices/individualSlice';
import { AppContainer, MainDisplayModal } from './styled-components/GlobalStyledComponents';
import { setJobsList } from './store/slices/jobSlice';
import MobileHeaderComponent from './components/SharedComponents/MobileHeaderComponent';
import { getDesignToken } from './theme/theme';
import { getTagStatus, thunkFetchTags } from './store/slices/keywordSlice';
import { addLogReport, setActivityList, thunkFetchActivity } from './store/slices/activitySlice';
import {
  fetchNotifications,
  notificationListenerConnected,
  startConnecting,
} from './store/slices/notificationSlice';
import PageNotFound from './pages/PageNotFound';
import PuffLoader from './components/UI/Animation/PuffLoader';
import { getAccessToken, setCredentials } from './store/slices/authSlice';
import MobileNavigationComponent from './components/NavigationComponents/MobileNavigationComponent';
import VerifiedRoute from './components/SharedComponents/VerifiedRoute';
import useDetectResize from './hooks/useDetectResize';
import NavigationComponent from './components/NavigationComponents/NavigationComponent';

import './App.css';

declare global {
  interface Window {
    tidioChatApi: any;
  }
}

const MyActivityPage = withAuthenticationRequired(
  React.lazy(() => import('./pages/MyActivityPage'))
);
const JobsPage = withAuthenticationRequired(React.lazy(() => import('./pages/JobsPage')));
const JobView = withAuthenticationRequired(React.lazy(() => import('./pages/JobView')));
const MyJobMatchesPage = withAuthenticationRequired(
  React.lazy(() => import('./pages/MyJobMatchesPage'))
);
const DashboardComponent = withAuthenticationRequired(
  React.lazy(() => import('./pages/DashboardComponent'))
);
const ChatComponent = withAuthenticationRequired(React.lazy(() => import('./pages/ChatComponent')));
const MyProfilePage = withAuthenticationRequired(React.lazy(() => import('./pages/MyProfilePage')));
const MyProfileFormPage = withAuthenticationRequired(
  React.lazy(() => import('./pages/MyProfileFormPage'))
);
const PostLoginPage = withAuthenticationRequired(React.lazy(() => import('./pages/PostLoginPage')));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));

const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

function App() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const keywordStatus = useSelector(getTagStatus);
  const { isLoading, isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const { token, isAuthorized } = useAppSelector(getAccessToken);

  //B2C User Store
  const userId = useSelector((state: RootState) => fetchMyId(state));
  const myDetails = useSelector((state: RootState) => fetchMyDetails(state));

  const { isDesktop, isLaptop, browserWindow, isMobile, isTablet } = useDetectResize();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const userData = useSelector((state: RootState) => rtkFetchUserInfo(state));

  const userCredential = useAppSelector((state: RootState) => fetchAccountEmail(state));

  //Notification Store
  const webSocketConnected = useSelector(notificationListenerConnected);

  const [mode, setMode] = React.useState<PaletteMode>('light');
  const theme = useMemo(() => createTheme(getDesignToken(mode)), [mode]);

  const [isHidden, setIsHidden] = useState<boolean>(true); //For menu display

  const isHiddenHandler = () => {
    setIsHidden((currState) => !currState);
  };

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode: PaletteMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    []
  );

  useEffect(() => {
    // Once individual Id is fetched, fetch jobs. If user.is_registered === false display post-login flow
    // user logs in to auth0
    // dispatch a userInfoFetch passing in the auth0 login email
    // unuwrap response and direct user to post-login page OR dashboard
    // remove dispatch fetchJobs and destructure response to store jobs in job slice, individual in individual slice etc

    console.info('MyDetails!', myDetails);
    console.info('User Data Set!', userData);
    console.info('My Record ID: ', userId);
    console.info('Auth0 user profile: ', user);
    console.info('Auth0 token: ', token);
    console.log(keywordStatus);

    if ((token === undefined || token === null) && user) {
      console.warn('auth0 loaded. set credentials');
      getAccessTokenSilently().then((accessToken: string) => {
        console.warn(accessToken);
        dispatch(setCredentials({ user: user, token: accessToken }));
        dispatch(setUserEmail({ email: user.email! }));
      });
    }
    console.log(userCredential);
    if (!isLoading && user && isAuthorized && userCredential) {
      setLoading(true);
      //Get userData after auth0 login and populate individualSlice.
      if (window.location.pathname === '/') navigate('/dashboard');

      dispatch(loadUserAndJobInfo({ userEmail: userCredential!, token: token! }))
        .unwrap()
        .then((res) => {
          console.info('loadUserAndJobInfo response: ', res);
          dispatch(
            updateLikedJobsAndAppliedJobs({ profile: res.data.user.profile, jobs: res.data.jobs })
          );
          dispatch(setUserData(res.data.user));
          dispatch(setJobsList(res.data.jobs));
          // TODO: process likedJobsArray in jobSlice to assign isJobActive states and return here to then dispatch to individual slice to update userSettings.likedJobsArr
        })
        .finally(() => {
          setLoading(false);
        });

      if (userId !== null) {
        // IMPORTANT, add token back when Auth0 is hooked up.
        dispatch(thunkFetchTags({ token: token! })) //dispatch(thunkFetchTags({token: token!}))
          .then(() => {
            //should decouple this to its own conditional.
            console.log('Retrieved Tags');
          });

        console.log('token: ', token);
        dispatch(thunkFetchActivity({ indId: userId, token: token! }))
          .unwrap()
          .then((res) => {
            //should decouple this to its own conditional.
            console.warn('Retrieved activity', res);
            dispatch(setActivityList(res));
          });

        console.warn('Adding Login Report!', userId);
        dispatch(
          addLogReport({
            activityObjArr: [{ field: 'login', value: dayjs().toISOString() }],
            info: {
              orgId: myDetails?.orgId!,
              provId: myDetails?.provId!,
              indId: myDetails?.indId!,
              tag: `login@${dayjs().format('YYYY-MM-DD')}`,
            },
            token: token!,
          })
        );
        //TODO log activity for login but insert record to activity_report ONLY if record DNE
      }

      if (!webSocketConnected && userId !== null) {
        dispatch(startConnecting());
        console.warn('retrieving notifications!', userId);
        dispatch(fetchNotifications({ b2cId: userId, token: token! }));
      }
    }
  }, [userId, user, token, isLoading, isAuthorized, myDetails.isVerified]);

  useEffect(() => {
    console.warn('isAuthenticated: ', isAuthenticated);
    console.warn('isLoading: ', isLoading);
    console.warn('auth0 user: ', user);
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    const checkTidioChatApi = setInterval(() => {
      // Check if tidioChatApi is defined in the window object
      if (
        window.tidioChatApi &&
        typeof window.tidioChatApi.open === 'function' &&
        typeof window.tidioChatApi.close === 'function'
      ) {
        const tidioIframe = document.querySelector('body');

        // Add event listeners if not already added
        if (!tidioIframe!.hasAttribute('data-tidio-listeners-added')) {
          tidioIframe!.addEventListener('mousedown', window.tidioChatApi.close);
          tidioIframe!.addEventListener('touchstart', window.tidioChatApi.close);
          tidioIframe!.setAttribute('data-tidio-listeners-added', 'true');
        }

        clearInterval(checkTidioChatApi);
      }
    }, 1500); // Check every second

    return () => {
      clearInterval(checkTidioChatApi);

      // Clean up event listeners
      const tidioIframe = document.querySelector('body');
      if (tidioIframe && window.tidioChatApi && typeof window.tidioChatApi.close === 'function') {
        tidioIframe.removeEventListener('mousedown', window.tidioChatApi.close);
        tidioIframe.removeEventListener('touchstart', window.tidioChatApi.close);
      }
    };
  }, []);

  if (isLoading || loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#E4F2F6',
        }}
      >
        <Box>
          <PuffLoader color="#0379A0" size={160} />
        </Box>
      </Box>
    );
  }
  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContainer
          display={isMobile || isTablet ? 'block' : 'inline-flex'}
          justifyContent={!myDetails.isVerified || isAuthenticated ? 'center' : 'unset'}
        >
          {(isDesktop || isLaptop) && myDetails.isVerified && isAuthenticated ? (
            <React.Suspense fallback={<Box sx={{ width: '100vw', height: '67px' }}></Box>}>
              <NavigationComponent isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
            </React.Suspense>
          ) : (
            !isHidden && (
              <React.Suspense fallback={<Box sx={{ width: '100vw', height: '67px' }}></Box>}>
                <MobileNavigationComponent
                  onToggleNavigation={isHiddenHandler}
                  theme={theme}
                  colorMode={colorMode}
                />
              </React.Suspense>
            )
          )}
          {isDesktop || isLaptop
            ? ''
            : myDetails.isVerified &&
              isAuthenticated && (
                <MobileHeaderComponent
                  theme={theme}
                  colorMode={colorMode}
                  onToggleNavigation={isHiddenHandler}
                />
              )}

          <MainDisplayModal
            className={
              myDetails.isVerified && isAuthenticated && !isLoading
                ? 'main-display-modal card'
                : 'non-verified-display'
            }
            height={browserWindow.h}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            isTablet={isTablet}
            isFormOrLandingPage={!myDetails.isVerified || !isAuthenticated}
          >
            <Routes>
              <Route
                path="/chat"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <ChatComponent />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/activity"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <MyActivityPage />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/jobs"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <JobsPage />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/jobs/:id"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <JobView />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/myjobmatches"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <MyJobMatchesPage />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <DashboardComponent />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/profile"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <MyProfilePage />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/profile/edit"
                element={
                  <React.Suspense>
                    <VerifiedRoute isVerified={myDetails.isVerified}>
                      <MyProfileFormPage />
                    </VerifiedRoute>
                  </React.Suspense>
                }
              />
              <Route
                path="/register/*"
                element={
                  <React.Suspense>
                    <PostLoginPage />
                  </React.Suspense>
                }
              />
              <Route
                path="/"
                element={
                  <React.Suspense>
                    <LandingPage />
                  </React.Suspense>
                }
              />
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </MainDisplayModal>
        </AppContainer>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
